import { useState, useEffect, type FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { contractsApi, departmentsApi, suppliersApi, type Contract, type Supplier, type BBBEEStatus, ApiError } from "@/lib/api";
import SupplierPicker from "@/components/feature/SupplierPicker";

const BBBEE_OPTIONS: BBBEEStatus[] = ["Level 1", "Level 2", "Level 3", "Level 4", "Level 5", "Level 6", "Level 7", "Level 8", "Non-Compliant", "Exempt (EME)"];

const CONTRACT_NUMBER_REGEX = /^[A-Z]{2,10}\/[A-Z0-9]{2,10}\/\d{4}-\d{3,6}$/i;

function validateContractNumber(value: string): string | null {
  if (!value) return null;
  return CONTRACT_NUMBER_REGEX.test(value) ? null : "Format must be DEPT/TYPE/YEAR-SEQ (e.g. DHA/ICT/2024-089)";
}

const CONTRACT_TYPES = [
  "ICT Services",
  "Professional Services",
  "Construction & Infrastructure",
  "Facility Management",
  "Healthcare",
  "Consulting",
  "Audit & Assurance",
  "Supply & Logistics",
  "Security Services",
  "Other",
];

type FormData = {
  supplier_id: number | null;
  supplier_name: string;
  vendor_number: string;
  bbbee_status: string;
  tax_pin: string;
  tax_clearance_validity: string;
  csd_number: string;
  contract_number: string;
  contract_type: string;
  contract_value: string;
  start_date: string;
  end_date: string;
  original_term_months: string;
  extension_allowed: boolean;
  extension_conditions: string;
  responsible_officer: string;
  department: string;
  description: string;
  notes: string;
};

const empty: FormData = {
  supplier_id: null,
  supplier_name: "",
  vendor_number: "",
  bbbee_status: "",
  tax_pin: "",
  tax_clearance_validity: "",
  csd_number: "",
  contract_number: "",
  contract_type: "",
  contract_value: "",
  start_date: "",
  end_date: "",
  original_term_months: "",
  extension_allowed: false,
  extension_conditions: "",
  responsible_officer: "",
  department: "",
  description: "",
  notes: "",
};

function contractToForm(c: Contract): FormData {
  return {
    supplier_id: c.supplier_id,
    supplier_name: c.supplier?.name ?? c.vendor_name,
    vendor_number: c.supplier?.vendor_number ?? "",
    bbbee_status: c.supplier?.bbbee_status ?? "",
    tax_pin: c.supplier?.tax_pin ?? "",
    tax_clearance_validity: c.supplier?.tax_clearance_validity ? c.supplier.tax_clearance_validity.slice(0, 10) : "",
    csd_number: c.supplier?.csd_number ?? "",
    contract_number: c.contract_number,
    contract_type: c.contract_type ?? "",
    contract_value: c.contract_value ?? "",
    start_date: c.start_date,
    end_date: c.end_date,
    original_term_months: c.original_term_months?.toString() ?? "",
    extension_allowed: c.extension_allowed,
    extension_conditions: c.extension_conditions ?? "",
    responsible_officer: c.responsible_officer ?? "",
    department: c.department,
    description: c.description ?? "",
    notes: c.notes ?? "",
  };
}

export default function ContractForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditing = !!id;

  const [formData, setFormData] = useState<FormData>(empty);
  const [departments, setDepartments] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const [loading, setLoading] = useState(isEditing);

  useEffect(() => {
    departmentsApi.list(1).then((res) => {
      setDepartments(res.data.data.map((d) => d.name));
    }).catch(() => {});

    if (isEditing && id) {
      contractsApi.get(Number(id))
        .then((res) => setFormData(contractToForm(res.data)))
        .catch((err) => setError(err.message))
        .finally(() => setLoading(false));
    }
  }, [id, isEditing]);

  const handleChange = (field: keyof FormData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => { const next = { ...prev }; delete next[field]; return next; });
  };

  const handleSupplierChange = (supplier: Supplier | null) => {
    setFormData((prev) => ({
      ...prev,
      supplier_id: supplier?.id ?? null,
      supplier_name: supplier?.name ?? "",
      vendor_number: supplier?.vendor_number ?? "",
      bbbee_status: supplier?.bbbee_status ?? "",
      tax_pin: supplier?.tax_pin ?? "",
      tax_clearance_validity: supplier?.tax_clearance_validity ? supplier.tax_clearance_validity.slice(0, 10) : "",
      csd_number: supplier?.csd_number ?? "",
    }));
    setFieldErrors((prev) => { const next = { ...prev }; delete next.supplier_id; return next; });
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});

    const numErr = validateContractNumber(formData.contract_number);
    if (numErr) {
      setFieldErrors({ contract_number: [numErr] });
      return;
    }
    setSubmitting(true);

    const payload = {
      supplier_id: formData.supplier_id,
      contract_number: formData.contract_number,
      contract_type: formData.contract_type || null,
      contract_value: formData.contract_value || null,
      start_date: formData.start_date,
      end_date: formData.end_date,
      original_term_months: formData.original_term_months ? Number(formData.original_term_months) : null,
      extension_allowed: formData.extension_allowed,
      extension_conditions: formData.extension_conditions || null,
      responsible_officer: formData.responsible_officer || null,
      department: formData.department,
      description: formData.description || null,
      notes: formData.notes || null,
    };

    try {
      if (isEditing && id) {
        await contractsApi.update(Number(id), payload);
      } else {
        await contractsApi.create(payload);
      }
      if (formData.supplier_id) {
        await suppliersApi.update(formData.supplier_id, {
          vendor_number: formData.vendor_number || null,
          bbbee_status: (formData.bbbee_status || null) as BBBEEStatus | null,
          tax_pin: formData.tax_pin || null,
          tax_clearance_validity: formData.tax_clearance_validity || null,
          csd_number: formData.csd_number || null,
        });
      }
      navigate("/contracts");
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.errors) setFieldErrors(err.errors);
        else setError(err.message);
      } else {
        setError("An unexpected error occurred.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-foreground-400 text-sm">
        <i className="ri-loader-4-line animate-spin mr-2"></i>Loading...
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate(-1)}
          className="w-8 h-8 rounded-md flex items-center justify-center border border-background-300 text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-line text-sm"></i>
        </button>
        <div>
          <h1 className="text-primary-600 text-xl md:text-2xl font-bold">
            {isEditing ? "Edit Contract" : "Add New Contract"}
          </h1>
          <p className="text-foreground-500 text-sm mt-1">
            {isEditing ? "Update contract details" : "Register a new contract in the system"}
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-accent-50 border border-accent-200 rounded-md px-4 py-3 flex items-center gap-2 mb-5">
          <i className="ri-error-warning-line text-accent-600 text-sm"></i>
          <p className="text-accent-700 text-sm">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="bg-white rounded-lg border border-background-200 p-5 md:p-6 mb-6">
          <h2 className="text-primary-600 text-base font-semibold mb-5">Contract Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField label="Supplier" required>
              <SupplierPicker
                value={formData.supplier_id}
                valueName={formData.supplier_name}
                onChange={handleSupplierChange}
                error={fieldErrors.supplier_id?.[0]}
              />
            </FormField>
            <FormField label="Contract / Bid Number" required error={fieldErrors.contract_number?.[0]}>
              <input
                type="text"
                value={formData.contract_number}
                onChange={(e) => {
                  handleChange("contract_number", e.target.value.toUpperCase());
                }}
                onBlur={(e) => {
                  const err = validateContractNumber(e.target.value);
                  if (err) setFieldErrors((prev) => ({ ...prev, contract_number: [err] }));
                }}
                placeholder="e.g. DHA/ICT/2024-089"
                className="form-input font-mono"
                required
              />
              {!fieldErrors.contract_number && (
                <p className="text-foreground-400 text-[11px] mt-1">Format: DEPT/TYPE/YEAR-SEQ</p>
              )}
            </FormField>

            <div className="md:col-span-2 border-t border-background-200 pt-4 mt-1">
              <h3 className="text-foreground-700 text-sm font-semibold mb-3">Supplier Compliance</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <FormField label="Vendor Number" error={fieldErrors.vendor_number?.[0]}>
                  <input
                    type="text"
                    value={formData.vendor_number}
                    onChange={(e) => handleChange("vendor_number", e.target.value)}
                    placeholder="e.g. V-00123"
                    className="form-input"
                  />
                </FormField>
                <FormField label="B-BBEE Status" error={fieldErrors.bbbee_status?.[0]}>
                  <select
                    value={formData.bbbee_status}
                    onChange={(e) => handleChange("bbbee_status", e.target.value)}
                    className="form-input cursor-pointer"
                  >
                    <option value="">—</option>
                    {BBBEE_OPTIONS.map((opt) => (<option key={opt} value={opt}>{opt}</option>))}
                  </select>
                </FormField>
                <FormField label="Tax PIN" error={fieldErrors.tax_pin?.[0]}>
                  <input
                    type="text"
                    value={formData.tax_pin}
                    onChange={(e) => handleChange("tax_pin", e.target.value)}
                    placeholder="SARS Tax Compliance PIN"
                    className="form-input"
                  />
                </FormField>
                <FormField label="Tax Clearance Validity" error={fieldErrors.tax_clearance_validity?.[0]}>
                  <input
                    type="date"
                    value={formData.tax_clearance_validity}
                    onChange={(e) => handleChange("tax_clearance_validity", e.target.value)}
                    className="form-input"
                  />
                </FormField>
                <FormField label="CSD Number" error={fieldErrors.csd_number?.[0]}>
                  <input
                    type="text"
                    value={formData.csd_number}
                    onChange={(e) => handleChange("csd_number", e.target.value)}
                    placeholder="e.g. MAAA0123456"
                    className="form-input"
                  />
                </FormField>
              </div>
            </div>

            <FormField label="Category" error={fieldErrors.contract_type?.[0]}>
              <select
                value={formData.contract_type}
                onChange={(e) => handleChange("contract_type", e.target.value)}
                className="form-input cursor-pointer"
              >
                <option value="">Select category</option>
                {CONTRACT_TYPES.map((c) => (<option key={c} value={c}>{c}</option>))}
              </select>
            </FormField>
            <FormField label="Contract Value (ZAR)" error={fieldErrors.contract_value?.[0]}>
              <input
                type="number"
                value={formData.contract_value}
                onChange={(e) => handleChange("contract_value", e.target.value)}
                placeholder="e.g. 12450000"
                className="form-input"
                min={0}
              />
            </FormField>
            <FormField label="Start Date" required error={fieldErrors.start_date?.[0]}>
              <input
                type="date"
                value={formData.start_date}
                onChange={(e) => handleChange("start_date", e.target.value)}
                className="form-input"
                required
              />
            </FormField>
            <FormField label="End Date" required error={fieldErrors.end_date?.[0]}>
              <input
                type="date"
                value={formData.end_date}
                onChange={(e) => handleChange("end_date", e.target.value)}
                className="form-input"
                required
              />
            </FormField>
            <FormField label="Original Term (Months)" error={fieldErrors.original_term_months?.[0]}>
              <input
                type="number"
                value={formData.original_term_months}
                onChange={(e) => handleChange("original_term_months", e.target.value)}
                placeholder="e.g. 36"
                className="form-input"
                min={1}
              />
            </FormField>
            <FormField label="Department" required error={fieldErrors.department?.[0]}>
              <select
                value={formData.department}
                onChange={(e) => handleChange("department", e.target.value)}
                className="form-input cursor-pointer"
                required
              >
                <option value="">Select department</option>
                {departments.map((d) => (<option key={d} value={d}>{d}</option>))}
              </select>
            </FormField>
            <FormField label="Responsible Officer" error={fieldErrors.responsible_officer?.[0]}>
              <input
                type="text"
                value={formData.responsible_officer}
                onChange={(e) => handleChange("responsible_officer", e.target.value)}
                placeholder="e.g. Thabo Molefe"
                className="form-input"
              />
            </FormField>
          </div>
        </div>

        {/* Extension Details */}
        <div className="bg-white rounded-lg border border-background-200 p-5 md:p-6 mb-6">
          <h2 className="text-primary-600 text-base font-semibold mb-5">Extension Details</h2>
          <div className="space-y-4">
            <label className="flex items-center gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.extension_allowed}
                onChange={(e) => handleChange("extension_allowed", e.target.checked)}
                className="w-4 h-4 rounded border-background-300 text-primary-600 focus:ring-primary-500 cursor-pointer"
              />
              <span className="text-foreground-700 text-sm font-medium">Extension Allowed</span>
            </label>
            {formData.extension_allowed && (
              <FormField label="Extension Conditions" error={fieldErrors.extension_conditions?.[0]}>
                <textarea
                  value={formData.extension_conditions}
                  onChange={(e) => handleChange("extension_conditions", e.target.value)}
                  placeholder="Describe the conditions under which an extension may be granted"
                  rows={3}
                  className="form-input resize-none"
                />
              </FormField>
            )}
          </div>
        </div>

        {/* Notes */}
        <div className="bg-white rounded-lg border border-background-200 p-5 md:p-6 mb-6">
          <h2 className="text-primary-600 text-base font-semibold mb-5">Additional Notes</h2>
          <FormField label="Notes" error={fieldErrors.notes?.[0]}>
            <textarea
              value={formData.notes}
              onChange={(e) => handleChange("notes", e.target.value)}
              placeholder="Any additional information..."
              rows={3}
              className="form-input resize-none"
            />
          </FormField>
        </div>

        {/* Document Upload placeholder */}
        <div className="bg-white rounded-lg border border-background-200 p-5 md:p-6 mb-6">
          <h2 className="text-primary-600 text-base font-semibold mb-5">Contract Document</h2>
          <div className="border-2 border-dashed border-background-300 rounded-lg p-6 text-center hover:border-primary-400 transition-colors cursor-pointer">
            <div className="w-10 h-10 rounded-full bg-foreground-100 flex items-center justify-center mx-auto mb-3">
              <i className="ri-upload-cloud-2-line text-foreground-500 text-lg"></i>
            </div>
            <p className="text-foreground-600 text-sm font-medium">
              Drag and drop your file here, or <span className="text-primary-600">browse</span>
            </p>
            <p className="text-foreground-400 text-xs mt-1">PDF files only, up to 25MB</p>
            <input type="file" accept=".pdf" className="hidden" />
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={submitting}
            className="h-11 px-6 bg-primary-600 text-white text-sm font-semibold rounded-md hover:bg-primary-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {submitting && <i className="ri-loader-4-line animate-spin text-sm"></i>}
            {isEditing ? "Update Contract" : "Create Contract"}
          </button>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="h-11 px-6 border border-background-300 text-foreground-600 text-sm font-medium rounded-md hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

function FormField({
  label,
  children,
  required,
  error,
}: {
  label: string;
  children: React.ReactNode;
  required?: boolean;
  error?: string;
}) {
  return (
    <div>
      <label className="block text-foreground-700 text-sm font-medium mb-1.5">
        {label}
        {required && <span className="text-accent-600 ml-0.5">*</span>}
      </label>
      {children}
      {error && <p className="text-accent-600 text-xs mt-1">{error}</p>}
    </div>
  );
}
